import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { first, map, tap } from 'rxjs';
import { Router, RouterLink } from '@angular/router';

import { AiAgent } from 'src/app/ai-agents/models/ai-agents.interface';
import { AiAgentContent, AiAgentGroupContent } from 'src/app/ai-agents/models/ai-agents-content.interface';
import { AiAgentsService } from 'src/app/ai-agents/services/ai-agents.service';
import { AiAgentEnum } from 'src/app/api/models/ai-agents/ai-agents.enum';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { IntercomService } from 'src/app/chat/intercom.service';
import { PermissionsService } from 'src/app/auth/permissions.service';
import { ProfileQuery } from 'src/app/profile/state/profile.query';
import { RoleEnum } from 'src/app/core/models/permission-role.enum';
import { RolePermissionsHelper } from 'src/app/auth/role-permmissions-helper';
import { RoutesManager } from 'src/app/shared/models/routes.interfaces';
import { AssessmentsUseCaseTabService } from 'src/app/ai-assessments/services/assessments-use-case-tab.service';
import { AssessmentUseCaseEnum } from 'src/app/ai-assessments/models/assessments.enum';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { ContentPipeModule } from 'src/app/services/content/content-pipe.module';
import { TippyModule } from 'src/app/shared/tippy.module';

import { Profile } from '../../profile/models/profile.interface';
import { HomeBox, HomeBoxItem, HomeBoxLink } from '../models/home-box.interface';
import { HomeMiraAgent } from '../models/home-mira-agent.interface';
import { HOME_BOXES } from './home-boxes.config';

@Component({
    selector: 'home-dashboard',
    standalone: true,
    templateUrl: './home-dashboard.component.html',
    styleUrls: ['./home-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ContentPipeModule,
        TippyModule,
        RouterLink,
    ],
})
export class HomeDashboardComponent implements OnInit {
    readonly spriteUrl = '../../../assets/img/sprite.svg';

    readonly miraAgentsRoute = ['/', RoutesManager.ai_agents];
    readonly miraPoliciesRoute = ['/', RoutesManager.ai_agents, RoutesManager.ai_agents_business_profile, RoutesManager.ai_agents_business_profile_policies];
    readonly miraProfileRoute = ['/', RoutesManager.ai_agents, RoutesManager.ai_agents_business_profile, RoutesManager.ai_agents_business_profile_general];

    title: string;
    boxes: HomeBox[] = [];
    showMira = false;
    miraAgents = signal<HomeMiraAgent[]>([]);

    private readonly destroyRef = inject(DestroyRef);

    /**
     * The per-use-case home links point at the label tab of the single Assessments table
     * (e.g. `/assessments/privacy`) instead of the legacy split-by-use-case pages.
     * RoPA keeps `/ropa-assessments`.
     */
    private readonly unifiedAssessmentRouteByLegacy: Readonly<Record<string, string[]>> = {
        [RoutesManager.compliance_assessments]: ['/', RoutesManager.assessments, AssessmentsUseCaseTabService.slugByLabel[AssessmentUseCaseEnum.Privacy]],
        [RoutesManager.vendor_assessments]: ['/', RoutesManager.assessments, AssessmentsUseCaseTabService.slugByLabel[AssessmentUseCaseEnum.Vendor]],
        [RoutesManager.ai_assessments]: ['/', RoutesManager.assessments, AssessmentsUseCaseTabService.slugByLabel[AssessmentUseCaseEnum.AI]],
    };

    /**
     * Some home links move under the new sidenav: Risk registry / Business impact become tabs of the
     * Risks shell (`/risks/risk-registry`, `/risks/business-impact`) and Policies moves under Settings
     * (`/company-settings/policies`).
     */
    private readonly newNavLinkRouteByLegacy: Readonly<Record<string, string[]>> = {
        [RoutesManager.risk_registry]: ['/', RoutesManager.risks, RoutesManager.risksRegistry],
        [RoutesManager.impactAssessment]: ['/', RoutesManager.risks, RoutesManager.risksBusinessImpact],
        [RoutesManager.policies]: ['/', RoutesManager.companySettings, RoutesManager.policies],
    };

    constructor(
        private profileQuery: ProfileQuery,
        private aiAgentsService: AiAgentsService,
        private featureFlagQuery: FeatureFlagQuery,
        private intercomService: IntercomService,
        private permissionsService: PermissionsService,
        private contentPipe: ContentPipe,
        private router: Router,
    ) { }

    ngOnInit(): void {
        const profile: Profile = this.profileQuery.getValue();
        const role = profile.role;

        this.title = this.buildGreeting(profile.firstName);
        this.boxes = this.resolveBoxes(role);
        this.showMira = this.permissionsService.getIsAllowed(RolePermissionsHelper.getPerrmitedRoleForAiAgents());

        if (this.showMira) {
            this.initMiraAgents();
        }
    }

    // Time-of-day greeting, e.g. "Good morning, Shila".
    private buildGreeting(name: string): string {
        const hour = new Date().getHours();
        const key = hour < 12 ? 'goodMorning' : hour < 18 ? 'goodAfternoon' : 'goodEvening';
        return this.contentPipe.transform(`home-v2026.${key}`, { params: { name } }) as string;
    }

    onAgentClick(agent: HomeMiraAgent): void {
        if (agent.isAvailable) {
            this.router.navigate([RoutesManager.ai_agents, agent.route]);
        } else {
            this.intercomService.show();
        }
    }

    private isVisibleForRole(roles: RoleEnum[] | undefined, role: RoleEnum): boolean {
        return !roles || roles.includes(role);
    }

    // Keep only boxes/items/links the current role may see; drop boxes left empty.
    private resolveBoxes(role: RoleEnum): HomeBox[] {
        return HOME_BOXES
            .filter(box => box.roles.includes(role))
            .map(box => ({
                ...box,
                items: box.items
                    .filter((item: HomeBoxItem) => this.isVisibleForRole(item.roles, role))
                    .map((item: HomeBoxItem) => this.withUnifiedAssessmentRoute(item)),
                links: box.links
                    ?.filter((link: HomeBoxLink) => this.isVisibleForRole(link.roles, role))
                    .map((link: HomeBoxLink) => this.withNewNavLinkRoute(link)),
            }))
            .filter(box => box.items.length > 0);
    }

    /** Repoint a per-use-case assessment item to its unified label tab. */
    private withUnifiedAssessmentRoute(item: HomeBoxItem): HomeBoxItem {
        const legacyRoute = item.route?.[item.route.length - 1];
        const unifiedRoute = this.unifiedAssessmentRouteByLegacy[legacyRoute];
        return unifiedRoute ? { ...item, route: unifiedRoute } : item;
    }

    /** Repoint Risk registry / Business impact / Policies links to their new-sidenav locations. */
    private withNewNavLinkRoute(link: HomeBoxLink): HomeBoxLink {
        const legacyRoute = link.route?.[link.route.length - 1];
        const newRoute = this.newNavLinkRouteByLegacy[legacyRoute];
        return newRoute ? { ...link, route: newRoute } : link;
    }

    private initMiraAgents(): void {
        this.aiAgentsService.initAgents();

        const catalog = (this.contentPipe.transform('ai-agents.agentsGroup') as AiAgentGroupContent[])
            .flatMap(group => group.agents)
            // Vendor risk score is intentionally excluded from the homepage Mira panel.
            .filter(agent => agent.miraId !== AiAgentEnum.VendorRiskScoreCreate);

        this.aiAgentsService.selectAiAgents().pipe(
            first(),
            map(agents => new Map(agents.map(agent => [agent.miraId, agent]))),
            map(availabilityByMira => this.buildMiraAgents(catalog, availabilityByMira)),
            tap(agents => this.miraAgents.set(agents)),
            takeUntilDestroyed(this.destroyRef),
        ).subscribe();
    }

    // Merge the Squidex agent catalog with BE availability + dev feature flags.
    private buildMiraAgents(catalog: AiAgentContent[], availabilityByMira: Map<AiAgentEnum, AiAgent>): HomeMiraAgent[] {
        return catalog.map(agent => ({
            miraId: agent.miraId,
            name: agent.name,
            icon: agent.icon,
            route: agent.route,
            isAvailable: agent.devFeatureFlagName
                ? this.featureFlagQuery.getFlag(agent.devFeatureFlagName)
                : (availabilityByMira.get(agent.miraId)?.isAvailable ?? false),
        }));
    }
}
